<template>
  <div class="fields-settings">
    <p class="fields-settings__title mb_16">
      <span class="fields-settings__title-left">{{ $t("insuranceCompanies.InputFields") }}</span>
      <span class="fields-settings__title-right">{{ $t("adminTitles.requiredField") }}</span>
    </p>
    <div
      class="fields-settings__container"
      :class="{ expanded: isFieldsExpanded || mergedFields.length < 10 }"
    >
      <el-checkbox-group
        class="fields-settings__checkboxes"
        :value="selectedFields"
        @input="onLeftChange"
      >
        <el-checkbox
          v-for="(field, i) in mergedFields"
          :label="field.name"
          :key="i + field.name"
        >
          {{ $t(InsuredFieldsSettingsTitle[field.name]) }}
        </el-checkbox>
      </el-checkbox-group>

      <el-checkbox-group
        class="fields-settings__required-checkboxes"
        :value="selectedRequiredFields"
        @input="onRightChange"
      >
        <el-checkbox
          v-for="(field, i) in mergedFields"
          :key="i + field.name"
          :label="field.name"
        >
          <span> </span>
        </el-checkbox>
      </el-checkbox-group>

      <div
        v-if="!isFieldsExpanded && mergedFields.length > 10"
        class="fields-settings__gradient"
      ></div>
    </div>

    <span
      v-if="mergedFields.length > 10"
      @click="isFieldsExpanded = !isFieldsExpanded"
      class="fields-settings__toggle"
    >
      {{ isFieldsExpanded ? $t("insuranceCompanies.ShowLess") : $t("insuranceCompanies.ShowMore") }}
    </span>

    <p class="fields-settings__title mb_16 mt_24">
      <span class="fields-settings__title-left">{{ $t("insuranceCompanies.Documents") }}</span>
      <span class="fields-settings__title-right">{{ $t("adminTitles.requiredField") }}</span>
    </p>
    <div class="fields-settings__container" :class="{ expanded: true }">
      <el-checkbox-group
        class="fields-settings__checkboxes"
        :value="selectedScans"
        @input="updateSelectedScans"
      >
        <el-checkbox v-for="(scan, i) in scansFields" :label="scan" :key="scan + i">
          {{ $t(InsuredFieldsSettingsTitle[scan]) }}
        </el-checkbox>
      </el-checkbox-group>
      <el-checkbox-group
        class="fields-settings__required-checkboxes"
        :value="selectedRequiredFields"
        @input="updateSelectedRequiredScans"
      >
        <el-checkbox
          v-for="(field, i) in scansFields"
          :key="field + i"
          :label="field"
        >
          <span> </span>
        </el-checkbox>
      </el-checkbox-group>
    </div>
  </div>
</template>

<script>
import { InsuredFieldsSettingsTitle } from '@/views/RenovaDashboard/SettingsParts/parts/InsuranceCompanies/components/constants';

export default {
  name: 'FieldSettings',
  props: {
    requiredFields: {
      type: Array,
      default: () => [],
    },
    additionalFields: {
      type: Array,
      default: () => [],
    },
    scansFields: {
      type: Array,
      default: () => [],
    },
    selectedFields: {
      type: Array,
      default: () => [],
    },
    selectedScans: {
      type: Array,
      default: () => [],
    },
    selectedRequiredFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isFieldsExpanded: false,
    };
  },
  computed: {
    InsuredFieldsSettingsTitle() {
      return InsuredFieldsSettingsTitle;
    },
    mergedFields() {
      return [
        ...this.requiredFields.map((fieldName) => ({
          name: fieldName,
          isRequired: true,
        })),
        ...this.additionalFields.map((fieldName) => ({
          name: fieldName,
          isRequired: false,
        })),
      ];
    },
  },
  methods: {
    onLeftChange(newLeft) {
      const oldLeft = [...this.selectedFields];
      const forced = [...this.requiredFields];
      const removed = oldLeft.filter((field) => !newLeft.includes(field));

      const triedRemoveForced = removed.filter((f) => forced.includes(f));
      if (triedRemoveForced.length) {
        this.$notify.error(this.$t('adminTitles.defaultFieldCannotBeRemoved'));
        this.$emit('update:selectedFields', oldLeft);
        return;
      }

      const finalLeft = Array.from(new Set([...forced, ...newLeft]));

      const added = finalLeft.filter((f) => !oldLeft.includes(f));
      const finalRight = [...this.selectedRequiredFields];

      if (added.length) {
        finalRight.push(...added);
      }

      removed.forEach((r) => {
        const idx = finalRight.indexOf(r);
        if (idx !== -1) {
          finalRight.splice(idx, 1);
        }
      });

      this.$emit('update:selectedFields', finalLeft);
      this.$emit('update:selectedRequiredFields', Array.from(new Set(finalRight)));
    },

    onRightChange(newRight) {
      const oldRight = [...this.selectedRequiredFields];
      const forced = [...this.requiredFields];
      const removed = oldRight.filter((f) => !newRight.includes(f));

      const triedRemoveForced = removed.filter((f) => forced.includes(f));
      if (triedRemoveForced.length) {
        this.$notify.error(this.$t('adminTitles.ThisIsADefaultField'));
        this.$emit('update:selectedRequiredFields', oldRight);
        return;
      }

      const finalRight = Array.from(new Set([...newRight, ...forced]));
      const finalLeft = [...this.selectedFields];
      finalRight.forEach((f) => {
        if (!finalLeft.includes(f)) {
          finalLeft.push(f);
        }
      });
      this.$emit('update:selectedFields', finalLeft);
      this.$emit('update:selectedRequiredFields', finalRight);
    },

    initializeSelectedFields() {
      const forced = [...this.requiredFields];
      const finalLeft = new Set([...this.selectedFields, ...forced]);
      const finalRight = new Set([...this.selectedRequiredFields, ...forced]);
      this.$emit('update:selectedFields', Array.from(finalLeft));
      this.$emit('update:selectedRequiredFields', Array.from(finalRight));
    },
    updateSelectedScans(value) {
      const oldLeft = [...this.selectedScans];
      const oldRight = [...this.selectedRequiredFields];

      const removed = oldLeft.filter((scan) => !value.includes(scan));
      removed.forEach((removedScan) => {
        const index = oldRight.indexOf(removedScan);
        if (index !== -1) {
          oldRight.splice(index, 1);
        }
      });

      this.$emit('update:selectedScans', value);
      this.$emit('update:selectedRequiredFields', oldRight);
    },
    updateSelectedRequiredScans(value) {
      const oldRight = [...this.selectedRequiredFields];
      const oldLeft = [...this.selectedScans];

      value.forEach((addedScan) => {
        if (!oldLeft.includes(addedScan)) {
          oldLeft.push(addedScan);
        }
      });

      this.$emit('update:selectedRequiredFields', value);
      this.$emit('update:selectedScans', oldLeft);
    }
  },
  created() {
    this.initializeSelectedFields();
  },
};

</script>

<style lang="scss">
.fields-settings {
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 8px;
  background-color: #efefef;

  &__title {
    display: flex;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;
    color: var(--solid-dark-purple);
  }
  &__title-left{
    display: flex;
    width: 100%;
  }
  &__title-right{
    text-align: center;
    width: 150px;
  }
  &__container {
    display: flex;
    position: relative;
    max-height: 480px;
    overflow: hidden;
    transition: max-height 0.3s ease;

    &.expanded {
      max-height: none;
    }
  }

  &__gradient {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 124px;
    background: linear-gradient(180deg, rgba(239, 239, 239, 0) 50.43%, #efefef 100%);
    pointer-events: none;
  }

  &__toggle {
    color: var(--solid-dark-purple);
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    text-align: left;
    text-decoration: underline;
    cursor: pointer;
    margin: 16px auto 0 auto;
  }

  &__checkboxes,
  &__required-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    .el-checkbox {
      margin-right: 0;
      display: flex;
      align-items: center;
    }

    .el-checkbox .el-checkbox__inner {
      width: 24px;
      height: 24px;
      box-shadow: 0px 4.8px 4.8px 0px #00000005;
      border: 1px solid transparent !important;
    }

    .el-checkbox .el-checkbox__inner::after {
      left: 5px;
      top: 5px;
      width: 14px;
      height: 14px;
    }

    .el-checkbox .el-checkbox__label {
      width: 100%;
      height: 40px;
      padding: 0 0 0 16px;
      display: flex;
      align-items: center;
      margin: 0 0 0 16px;
      background: white;
      border-radius: 8px 0px 0px 8px;
      color: var(--solid-dark-purple) !important;
      transition: color 0.3s;
    }

    .el-checkbox__input.is-checked + .el-checkbox__label {
      color: var(--solid-black-purple) !important;
    }

    .el-checkbox__input.is-disabled + span.el-checkbox__label {
      cursor: default;
    }

    .el-checkbox__input.is-disabled .el-checkbox__inner {
      cursor: default;
    }

    .el-checkbox__input.is-disabled .el-checkbox__inner::after {
      cursor: default;
    }

    .el-checkbox__input.is-disabled .el-checkbox__inner {
      background-color: var(--solid-purple);
    }
  }

  &__required-checkboxes {
    width: 150px;
    display: flex;
    .fields-settings__required-checkbox--required{
      opacity: 0.8;
    }
    .el-checkbox__label {
      width: 0px !important;
      padding: 0px !important;
      display: none !important;
    }
    .el-checkbox .el-checkbox__inner {
      border: 1px solid #D7D4E4 !important;
      box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.02) !important;
    }
    .el-checkbox {
      display: flex;
      justify-content: center;
      align-items: center;
      background: #FFFFFF;
      width: 100%;
      border-radius: 0px 8px 8px 0px;
      min-height: 40px;
      padding-top: 4px;
    }
  }
}
</style>
